import { inject, Injectable } from '@angular/core';
import * as pdfjs from 'pdfjs-dist';
import { PDF_WORKER_SRC } from '@iupics-util/tools/pdf-worker.util';
import { firstValueFrom } from 'rxjs';
import { DocumentsService } from './documents.service';

/** files kept in memory to reopen instantly (total bytes) */
const CACHE_MAX = 80 * 1024 * 1024;
/** thumbnails are drawn only for files up to these sizes (the whole file is downloaded) */
const THUMB_IMAGE_MAX = 8 * 1024 * 1024;
const THUMB_PDF_MAX = 15 * 1024 * 1024;
/** thumbnails drawn at the same time */
const THUMB_CONCURRENCY = 3;
const THUMB_WIDTH = 240;
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'avif'];

/**
 * Files of the Documents window shared by the preview and the thumbnails: the last opened files (blob cache,
 * bounded), and the thumbnails (images, first page of PDFs) drawn in the browser, a few at a time, kept for the
 * session.
 */
@Injectable({ providedIn: 'root' })
export class DocumentFilesService {
  readonly #docs = inject(DocumentsService);
  readonly #blobs = new Map<string, Blob>();
  readonly #thumbs = new Map<string, string | null>();
  readonly #pending = new Map<string, Promise<string | null>>();
  readonly #queue: (() => void)[] = [];
  #running = 0;

  cached(docId: string): Blob | undefined {
    const blob = this.#blobs.get(docId);
    if (blob) {
      // most recent last
      this.#blobs.delete(docId);
      this.#blobs.set(docId, blob);
    }
    return blob;
  }

  remember(docId: string, blob: Blob): void {
    this.#blobs.delete(docId);
    this.#blobs.set(docId, blob);
    let total = 0;
    for (const b of this.#blobs.values()) total += b.size;
    for (const [key, b] of this.#blobs) {
      if (total <= CACHE_MAX || this.#blobs.size <= 1) break;
      this.#blobs.delete(key);
      total -= b.size;
    }
  }

  /** a changed file (new version, restore): its cached files and thumbnails dropped (keys docId#version) */
  forget(docId: string): void {
    const prefix = `${docId}#`;
    for (const key of [...this.#blobs.keys()]) if (key.startsWith(prefix)) this.#blobs.delete(key);
    for (const key of [...this.#thumbs.keys()]) if (key.startsWith(prefix)) this.#thumbs.delete(key);
  }

  /** can a thumbnail be drawn for this file */
  hasThumb(fileName: string, size: number | null): boolean {
    const ext = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
    if (size === null) return false;
    if (IMAGE_EXT.includes(ext)) return size <= THUMB_IMAGE_MAX;
    return ext === 'pdf' && size <= THUMB_PDF_MAX;
  }

  /** thumbnail URL (data URL), null when it cannot be drawn; cached by key (docId#version) */
  thumb(docId: string, fileName: string, size: number | null, key: string): Promise<string | null> {
    if (this.#thumbs.has(key)) return Promise.resolve(this.#thumbs.get(key) ?? null);
    if (!this.hasThumb(fileName, size)) return Promise.resolve(null);
    const pending = this.#pending.get(key);
    if (pending) return pending;
    const p = new Promise<string | null>((resolve) => {
      this.#queue.push(() => {
        this.#running++;
        this.#draw(docId, fileName, key)
          .then((url) => {
            this.#thumbs.set(key, url);
            resolve(url);
          })
          .catch(() => {
            this.#thumbs.set(key, null);
            resolve(null);
          })
          .finally(() => {
            this.#running--;
            this.#pending.delete(key);
            this.#next();
          });
      });
      this.#next();
    });
    this.#pending.set(key, p);
    return p;
  }

  #next(): void {
    while (this.#running < THUMB_CONCURRENCY && this.#queue.length) this.#queue.shift()!();
  }

  async #draw(docId: string, fileName: string, key: string): Promise<string | null> {
    const blob = this.cached(key) ?? (await firstValueFrom(this.#docs.content(docId, true)));
    if (!this.#blobs.has(key)) this.remember(key, blob);
    const ext = fileName.split('.').pop()!.toLowerCase();
    if (ext !== 'pdf') return this.#imageThumb(blob);
    pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
    // isEvalSupported false: pdf.js 3.x compiles font code with eval (CVE-2024-4367, script run by a crafted PDF)
    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()), isEvalSupported: false }).promise;
    try {
      const page = await pdf.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: (THUMB_WIDTH * Math.min(2, window.devicePixelRatio || 1)) / base.width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) return null;
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL('image/jpeg', 0.75);
    } finally {
      pdf.destroy();
    }
  }

  /** small copy of an image (a big photo is not kept at full size for a thumbnail) */
  async #imageThumb(blob: Blob): Promise<string | null> {
    const bitmap = await createImageBitmap(blob);
    try {
      const scale = Math.min(1, (THUMB_WIDTH * Math.min(2, window.devicePixelRatio || 1)) / bitmap.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      if (!context) return null;
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.8);
    } finally {
      bitmap.close();
    }
  }
}
